import { useMemo } from 'react';
import { format } from 'date-fns';
import { useUserAgendaConfig } from './useUserAgendaConfig';
import { useInstructorRequests } from './useClassRequests';

interface CentralizedSlot {
  startTime: string;
  endTime: string;
  slotCount: number;
  standardDuration: number;
}

interface UseCentralizedSlotsProps {
  instructorId: string;
  selectedDate: Date;
  classes?: any[];
  trainings?: any[];
}

export const useCentralizedSlots = ({ 
  instructorId, 
  selectedDate, 
  classes = [], 
  trainings = [] 
}: UseCentralizedSlotsProps) => {
  // Get instructor's agenda configuration
  const { config: agendaConfig, isLoading: agendaLoading } = useUserAgendaConfig(instructorId);
  const { data: instructorRequests = [] } = useInstructorRequests(instructorId);

  // Default agenda configuration as fallback
  const defaultAgendaConfig = {
    working_days: ['mon', 'tue', 'wed', 'thu', 'fri'],
    working_hours: {
      mon: { start: '08:00', end: '18:00' },
      tue: { start: '08:00', end: '18:00' },
      wed: { start: '08:00', end: '18:00' },
      thu: { start: '08:00', end: '18:00' },
      fri: { start: '08:00', end: '18:00' }
    },
    default_duration: 60,
    is_public: true
  };

  // Use real agenda config or fallback to default
  const effectiveAgendaConfig = agendaConfig || defaultAgendaConfig;

  // Normalize time format for comparison (remove seconds if present)
  const normalizeTime = (timeStr: string) => {
    return timeStr.split(':').slice(0, 2).join(':');
  };

  const slots = useMemo(() => {
    if (!instructorId || !effectiveAgendaConfig) return [];

    // Check if date is in the past - return empty array for past dates
    const today = new Date();
    const isPastDate = selectedDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    if (isPastDate) return [];

    try {
      // Generate slots dynamically from agenda config
      const dayOfWeek = selectedDate.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
      
      // Check if the selected day is a working day
      if (!effectiveAgendaConfig.working_days.includes(dayOfWeek)) {
        return [];
      }

      const workingHours = effectiveAgendaConfig.working_hours[dayOfWeek];
      if (!workingHours) {
        return [];
      }

      // Convert times to 24-hour format
      const convertTimeTo24Hour = (timeString: string): string => {
        const [time, period] = timeString.split(' ');
        const [hours, minutes] = time.split(':').map(Number);
        
        let adjustedHours = hours;
        if (period === 'PM' && hours !== 12) {
          adjustedHours += 12;
        } else if (period === 'AM' && hours === 12) {
          adjustedHours = 0;
        }
        
        return `${adjustedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      };

      const openTime = convertTimeTo24Hour(workingHours.start);
      const closeTime = convertTimeTo24Hour(workingHours.end);
      const standardDuration = effectiveAgendaConfig.default_duration || 60;

      // Get all existing classes/trainings for this date
      const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
      
      // Combine classes and trainings
      const allActivities = [...classes, ...trainings].filter(activity => {
        if (!activity.start_time) return false;
        
        const activityDate = new Date(activity.start_time);
        const activityDateString = new Date(activityDate.getFullYear(), activityDate.getMonth(), activityDate.getDate()).toISOString().split('T')[0];
        return activityDateString === selectedDateStr;
      });

      // Generate time slots
      const generatedSlots: CentralizedSlot[] = [];
      const [openHour, openMinute] = openTime.split(':').map(Number);
      const [closeHour, closeMinute] = closeTime.split(':').map(Number);
      
      const openMinutes = openHour * 60 + openMinute;
      const closeMinutes = closeHour * 60 + closeMinute;
      
      for (let currentMinutes = openMinutes; currentMinutes < closeMinutes; currentMinutes += standardDuration) {
        const startHour = Math.floor(currentMinutes / 60);
        const startMinute = currentMinutes % 60;
        const endMinutes = currentMinutes + standardDuration;
        const endHour = Math.floor(endMinutes / 60);
        const endMinute = endMinutes % 60;
        
        const startTime = `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;
        const endTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
        
        // Check if slot conflicts with any existing activity
        const hasConflict = allActivities.some(activity => {
          const activityStart = new Date(activity.start_time);
          const activityEnd = new Date(activity.end_time);
          
          const activityStartMinutes = activityStart.getHours() * 60 + activityStart.getMinutes();
          const activityEndMinutes = activityEnd.getHours() * 60 + activityEnd.getMinutes();
          
          // Check for time overlap
          return (currentMinutes < activityEndMinutes && endMinutes > activityStartMinutes);
        });
        
        // Check if slot conflicts with any approved request
        const hasApprovedRequest = instructorRequests.some(request => {
          if (request.requested_date !== selectedDateStr || request.status !== 'approved') {
            return false;
          }
          
          // Check for time overlap
          const requestStart = request.requested_start_time;
          const requestEnd = request.requested_end_time;
          
          // Convert times to minutes for easier comparison
          const requestStartMinutes = parseInt(requestStart.split(':')[0]) * 60 + parseInt(requestStart.split(':')[1]);
          const requestEndMinutes = parseInt(requestEnd.split(':')[0]) * 60 + parseInt(requestEnd.split(':')[1]);
          
          // Check if there's any overlap
          return (currentMinutes < requestEndMinutes && endMinutes > requestStartMinutes);
        });
        
        // Filter out past slots - only show future slots
        const now = new Date();
        const todayStr = format(now, 'yyyy-MM-dd');
        const isToday = selectedDateStr === todayStr;
        
        if (isToday) {
          const currentTime = now.getHours() * 60 + now.getMinutes();
          
          // Skip slots that have already started (no buffer)
          if (currentMinutes <= currentTime) {
            continue;
          }
        }
        
        // Only add slot if it doesn't have conflicts
        if (!hasConflict && !hasApprovedRequest) {
          generatedSlots.push({
            startTime,
            endTime,
            slotCount: 1,
            standardDuration
          });
        }
      }
      
      return generatedSlots;
    } catch (error) {
      console.error('Error calculating centralized slots:', error);
      return [];
    }
  }, [instructorId, selectedDate, effectiveAgendaConfig, classes, trainings, instructorRequests]);

  return {
    slots,
    loading: agendaLoading,
    agendaConfig: effectiveAgendaConfig,
    normalizeTime
  };
};

